import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DataType } from '../../../shared/ontology/model/data-type';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JhiEventManager } from '../../../shared/core-utils';
import { ScaleService } from '../../../shared/ontology/service/scale.service';
import { OntologyContext } from '../../ontology.context';
import { ScaleDetails } from '../../../shared/ontology/model/scale-details';
import { PopupService } from '../../../shared/modal/popup.service';
import { DataTypeEnum } from '../../../shared/ontology/data-type.enum';
import { finalize } from 'rxjs/internal/operators/finalize';
import { AlertService } from '../../../shared/alert/alert.service';
import { ValidValues } from '../../../shared/ontology/model/valid-values';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-scale-dialog',
    templateUrl: './scale-dialog.component.html'
})
export class ScaleDialogComponent implements OnInit, OnDestroy {
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;
    protected readonly CATEGORY_VALUE_TEXT_LIMIT = 255;
    protected readonly MIN_RANGE = -999999999;
    protected readonly MAX_RANGE = 999999999;
    protected readonly SPECIAL_CHARACTERS = '^[^<>?|]*$';

    protected readonly ModeView = ModeView;
    protected readonly NUM_EDITABLE_FIELDS = 3;

    dataTypes: DataType[];
    isLoading: boolean;
    showRangeWidget: boolean;
    showCategoricalWidget: boolean;
    modeView: ModeView;
    scaleDetails!: ScaleDetails;
    scaleId: string | null = null;
    variableId: string | null = null;

    constructor(public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private router: Router,
                private eventManager: JhiEventManager,
                private scaleService: ScaleService,
                private alertService: AlertService,
                private ontologyService: OntologyService,
                private ontologyContext: OntologyContext,
                private translateService: TranslateService) {
        this.scaleDetails = new ScaleDetails();
        this.scaleDetails.validValues = new ValidValues();
    }

    ngOnInit(): void {
        this.resetWidgets();
        this.modeView = this.scaleId ? ModeView.VIEW : ModeView.CREATE;
        if (this.modeView === ModeView.VIEW) {
            this.isLoading = true;
            this.scaleService.getScaleId(this.scaleId).pipe(
                finalize(() => this.isLoading = false)).toPromise().then(scaleDetails => {
                this.ontologyContext.scaleDetails = structuredClone(scaleDetails);
                this.scaleDetails = structuredClone(scaleDetails);
                this.showRangeWidget = this.scaleDetails.dataType.name === DataTypeEnum.NUMERIC;
                this.showCategoricalWidget = this.scaleDetails.dataType.name === DataTypeEnum.CATEGORICAL;
                if (this.showCategoricalWidget) {
                    const re = new RegExp(this.SPECIAL_CHARACTERS);
                    this.scaleDetails.validValues.categories.forEach(category => {
                        category.editable = category && !re.test(category.description) ? true : category.editable;
                    });
                }
            });
        }
        this.isLoading = true;
        this.ontologyService.getNonSystemDataTypes().pipe(
            finalize(() => this.isLoading = false)).toPromise().then((dataTypes) => {
            this.dataTypes = dataTypes;
        });
    }

    edit() {
        this.modeView = ModeView.EDIT;
    }

    discard() {
        if (this.modeView === ModeView.CREATE) {
            this.clear();
        } else {
            this.modeView = ModeView.VIEW;
            this.scaleDetails = structuredClone(this.ontologyContext.scaleDetails);
        }
    }

    isDeletable() {
        return this.scaleDetails?.metadata?.deletable;
    }

    ngOnDestroy(): void {
        this.ontologyContext.scaleDetails = null;
    }

    clear() {
        this.navigateToVariableView();
    }

    save() {
        this.isLoading = true;
        if (this.modeView === ModeView.EDIT) {
            this.scaleService.update(this.scaleDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.scales.modal.scale.edited.success');
                this.notifyChanges();
            });
        } else {
            this.scaleService.create(this.scaleDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe((res) => {
                this.scaleDetails.id = res.body?.id;
                this.alertService.success('ontology-manager.tab.scales.modal.scale.created.success');
                this.notifyChanges();
            });
        }
    }

    isFormValid(f) {
        return f.form.valid && !this.isLoading && this.scaleDetails.name && this.scaleDetails.dataType;
    }

    protected onDataTypeChanged(dataType: any): void {
        this.resetWidgets();
        this.scaleDetails.dataType = this.dataTypes.find(dt => dt.id === dataType.id);
        if (this.scaleDetails.dataType.name === DataTypeEnum.NUMERIC) {
            this.showRangeWidget = true;
            this.scaleDetails.validValues.min = null;
            this.scaleDetails.validValues.max = null;
        } else if (this.scaleDetails.dataType.name === DataTypeEnum.CATEGORICAL) {
            this.showCategoricalWidget = true;
            this.scaleDetails.validValues.categories = [];
        }
    }

    private resetWidgets() {
        this.showRangeWidget = this.showCategoricalWidget = false;
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'scalePaneViewChanged' });
        this.clear();
    }

    protected showNoEditableFieldAlert() {
        return this.modeView === ModeView.EDIT && this.scaleDetails?.metadata?.editableFields.length < this.NUM_EDITABLE_FIELDS;
    }

    protected showNoneEditableCategoriesAlert() {
        return this.modeView === ModeView.EDIT && this.scaleDetails?.validValues?.categories?.some(
            category => !category.editable);
    }

    protected delete() {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        const message = 'ontology-manager.scale.confirm.deletion';
        confirmModalRef.componentInstance.message = this.translateService.instant(message);
        confirmModalRef.result.then(() => {
            this.isLoading = true;
            this.scaleService.delete(this.scaleDetails.id).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.scales.modal.scale.deleted.success');
                this.notifyChanges();
            });
        });
    }

    private navigateToVariableView() {
        this.activeModal.dismiss('cancel');
        setTimeout(() => {
            if (this.variableId) {
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', this.variableId, 'view'] } }],
                    { queryParamsHandling: 'merge' }
                );
            } else if (this.ontologyContext.variableDetails) {
                if (this.scaleDetails.id) {
                    this.ontologyContext.variableDetails.scale = structuredClone(this.scaleDetails);
                }
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', 'new'] } }],
                    { queryParamsHandling: 'merge' }
                );
            }
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapePressed(event: KeyboardEvent): void {
        event.preventDefault();
        this.navigateToVariableView();

    }
}

@Component({
    selector: 'jhi-scale-popup',
    template: ''
})
export class ScalePopupComponent implements OnInit {

    constructor(private popupService: PopupService,
                private route: ActivatedRoute) {
    }

    ngOnInit(): void {
        const id = this.route.snapshot.params['id'];
        const variableId = this.route.snapshot.params['variableId'];
        const modalRef = this.popupService.open(ScaleDialogComponent as Component, { modalDialogClass: 'modal-lg', backdrop: 'static' });
        modalRef?.then(ngmodalRef => {
            ngmodalRef.componentInstance.scaleId = id ? id : null;
            ngmodalRef.componentInstance.variableId = variableId ? variableId : null;
        });
    }

}
